import React from 'react';

/**
 * BOTANICAL LINE ART COMPONENTS
 * High visibility editorial botanical illustrations
 * Matching the exact style from the CSS reference
 */

// Simple leaf stem with editorial swirl and leaves
export const BotanicalLeaf = ({ style, ...props }) => (
  <svg
    width="120"
    height="80"
    viewBox="0 0 120 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#A67C61" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4">
      {/* Main stem */}
      <path d="M60 20 L60 70" />
      {/* Leaves along stem */}
      <path d="M52 35 Q60 30 68 35" />
      <path d="M50 45 Q60 40 70 45" />
      <path d="M48 55 Q60 50 72 55" />
      <path d="M50 65 Q60 60 70 65" />
    </g>
  </svg>
);

// Curved botanical stem with spiral
export const BotanicalStem = ({ style, ...props }) => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#D4B59E" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.35">
      {/* Curved stem */}
      <path d="M50 20 Q60 50 50 80" />
      {/* Small leaves */}
      <path d="M48 35 Q45 30 50 35" />
      <path d="M52 50 Q55 45 50 50" />
      <path d="M48 65 Q45 60 50 65" />
    </g>
  </svg>
);

// Branch with swirly leaf shapes
export const BotanicalBranch = ({ style, ...props }) => (
  <svg
    width="140"
    height="100"
    viewBox="0 0 140 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#A67C61" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4">
      {/* Main branch */}
      <path d="M20 50 L120 50" />
      {/* Leaf sprigs */}
      <path d="M50 45 Q55 35 60 45" />
      <path d="M70 42 Q75 32 80 42" />
      <path d="M90 45 Q95 35 100 45" />
    </g>
  </svg>
);

// Simple accent circle
export const BotanicalCircle = ({ style, ...props }) => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#E8C4A8" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.5">
      <circle cx="50" cy="50" r="30"/>
      <circle cx="50" cy="50" r="20" opacity="0.6"/>
    </g>
  </svg>
);

// Minimalist leaf outline
export const BotanicalLeafOutline = ({ style, ...props }) => (
  <svg
    width="80"
    height="100"
    viewBox="0 0 80 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#A67C61" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.38">
      {/* Leaf outline */}
      <path d="M40 15 C25 30 20 50 40 85 C60 50 55 30 40 15" />
      {/* Center vein */}
      <path d="M40 15 L40 85" />
      {/* Side veins */}
      <path d="M40 35 Q30 40 25 45" />
      <path d="M40 50 Q30 53 22 57" />
      <path d="M40 65 Q30 68 25 73" />
    </g>
  </svg>
);

// Swirl/spiral botanical accent
export const BotanicalSpiral = ({ style, ...props }) => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#D4B59E" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.35">
      {/* Spiral curve */}
      <path d="M60 30 Q80 40 85 60 Q80 80 60 88 Q40 80 35 60 Q40 45 55 38" />
      <path d="M60 45 Q70 50 72 60 Q70 70 60 75" />
    </g>
  </svg>
);

// Decorative corner
export const BotanicalCorner = ({ style, ...props }) => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#A67C61" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.35">
      {/* Corner stem */}
      <path d="M20 60 L20 20 L60 20" />
      {/* Small leaves */}
      <path d="M30 20 Q35 15 40 20" />
      <path d="M50 20 Q55 15 60 20" />
      <path d="M20 30 Q15 35 20 40" />
      <path d="M20 50 Q15 55 20 60" />
    </g>
  </svg>
);

// Large hero botanical composition
export const BotanicalHero = ({ style, ...props }) => (
  <svg
    width="300"
    height="300"
    viewBox="0 0 300 300"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      zIndex: 0,
      pointerEvents: 'none',
      ...style,
    }}
    {...props}
  >
    <g stroke="#A67C61" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.3">
      {/* Main stems */}
      <path d="M120 50 L120 150" />
      <path d="M180 70 L180 160" />
      
      {/* Leaves on first stem */}
      <path d="M112 80 Q120 75 128 80" />
      <path d="M110 100 Q120 95 130 100" />
      <path d="M108 120 Q120 115 132 120" />
      <path d="M110 140 Q120 135 130 140" />
      
      {/* Leaves on second stem */}
      <path d="M172 95 Q180 90 188 95" />
      <path d="M170 115 Q180 110 190 115" />
      <path d="M168 135 Q180 130 192 135" />
      
      {/* Curved branch */}
      <path d="M80 180 Q150 160 220 180" />
      <path d="M120 170 Q125 160 130 170" />
      <path d="M170 170 Q175 160 180 170" />
    </g>
  </svg>
);

// Demo Component
const BotanicalDemo = () => {
  return (
    <div className="relative w-full min-h-screen bg-gradient-to-br from-orange-100 via-amber-50 to-orange-50 overflow-hidden">
      {/* Background decorations - MORE VISIBLE */}
      <BotanicalHero style={{ left: -50, top: 100, opacity: 1 }} />
      <BotanicalHero style={{ right: -80, bottom: 50, opacity: 1, transform: 'scaleX(-1)' }} />
      
      {/* Main content */}
      <div className="relative z-10 max-w-6xl mx-auto px-8 py-20">
        
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-orange-600 to-orange-700 rounded-3xl p-16 mb-12 overflow-hidden">
          <BotanicalSpiral style={{ right: 40, top: 40, opacity: 1 }} />
          <BotanicalLeaf style={{ left: 30, bottom: 30, opacity: 1 }} />
          
          <div className="relative z-10 text-center text-white">
            <h1 className="text-6xl font-bold mb-6">Botanical Line Art</h1>
            <p className="text-xl text-orange-100 max-w-2xl mx-auto">
              High visibility editorial botanical illustrations with prominent strokes
            </p>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          
          {/* Card 1 */}
          <div className="relative bg-white rounded-2xl p-8 shadow-lg overflow-hidden">
            <BotanicalBranch style={{ right: -20, top: -10, opacity: 1 }} />
            <BotanicalCorner style={{ left: 0, bottom: 0, opacity: 1 }} />
            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-orange-800 mb-4">Visible Stems</h3>
              <p className="text-gray-700">
                Bold, clear strokes that are prominently displayed with higher opacity
              </p>
            </div>
          </div>

          {/* Card 2 */}
          <div className="relative bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 shadow-lg overflow-hidden">
            <BotanicalCircle style={{ right: 10, top: 10, opacity: 1 }} />
            <BotanicalLeafOutline style={{ left: -10, bottom: -20, opacity: 1 }} />
            <div className="relative z-10 text-white">
              <h3 className="text-2xl font-bold mb-4">Strong Lines</h3>
              <p className="text-orange-50">
                Thicker stroke widths (2-2.5px) for better visibility and editorial impact
              </p>
            </div>
          </div>

          {/* Card 3 */}
          <div className="relative bg-white rounded-2xl p-8 shadow-lg overflow-hidden">
            <BotanicalStem style={{ right: 20, top: 20, opacity: 1 }} />
            <BotanicalSpiral style={{ left: -20, bottom: -20, opacity: 1 }} />
            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-orange-800 mb-4">Editorial Style</h3>
              <p className="text-gray-700">
                Matching your CSS reference with opacity values of 0.3-0.5
              </p>
            </div>
          </div>
        </div>

        {/* Large Feature Section */}
        <div className="relative bg-white rounded-3xl p-16 shadow-xl overflow-hidden">
          <BotanicalLeaf style={{ left: 40, top: 40, opacity: 1 }} />
          <BotanicalBranch style={{ right: 50, top: 60, opacity: 1 }} />
          <BotanicalLeafOutline style={{ left: 60, bottom: 40, opacity: 1 }} />
          <BotanicalStem style={{ right: 80, bottom: 50, opacity: 1 }} />
          
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="text-5xl font-bold text-orange-800 mb-6">
              Highly Visible Line Art
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              These botanical elements feature thicker strokes (2-2.5px), higher opacity (0.3-0.5), 
              and colors that match your CSS exactly: #A67C61 (brown), #D4B59E (tan), and #E8C4A8 (peach). 
              Simple, clean lines inspired by editorial minimalist design.
            </p>
            <button className="bg-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-700 transition-colors">
              Explore Collection
            </button>
          </div>
        </div>

        {/* Color Reference Section */}
        <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-orange-800 mb-6 text-center">Color & Style Reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full mx-auto mb-3" style={{ backgroundColor: '#A67C61' }}></div>
              <p className="font-semibold text-gray-800">#A67C61</p>
              <p className="text-sm text-gray-600">Brown - Primary</p>
              <p className="text-xs text-gray-500 mt-1">Stroke Width: 2px</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full mx-auto mb-3" style={{ backgroundColor: '#D4B59E' }}></div>
              <p className="font-semibold text-gray-800">#D4B59E</p>
              <p className="text-sm text-gray-600">Tan - Secondary</p>
              <p className="text-xs text-gray-500 mt-1">Stroke Width: 2px</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full mx-auto mb-3" style={{ backgroundColor: '#E8C4A8' }}></div>
              <p className="font-semibold text-gray-800">#E8C4A8</p>
              <p className="text-sm text-gray-600">Peach - Accent</p>
              <p className="text-xs text-gray-500 mt-1">Stroke Width: 2.5px</p>
            </div>
          </div>
          <div className="mt-6 text-center text-gray-600">
            <p className="text-sm">Opacity Range: 0.3 - 0.5 for optimal visibility</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export all components
export const Botanical = {
  Leaf: BotanicalLeaf,
  Stem: BotanicalStem,
  Branch: BotanicalBranch,
  Circle: BotanicalCircle,
  LeafOutline: BotanicalLeafOutline,
  Spiral: BotanicalSpiral,
  Corner: BotanicalCorner,
  Hero: BotanicalHero,
};

export default BotanicalDemo;